import { ActionMenu, ActionMenuItem } from '@systemeio/ui'
import { nonDraggableProps } from 'modules/courses/courses/curriculum/utils/non-draggable-props'
import React, { memo, useState } from 'react'
import Confirmation from 'shared/components/confirmation-modal'
import { RequestModuleEnum } from 'shared/enums/request-module-enum'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { useRevalidateModules } from 'shared/hooks/use-revalidate-modules'
import { KeyedMutator } from 'swr'
import { ModuleId } from '../../curriculum/types/module-interface'
import { useLightCourseActions } from '../hooks/use-light-course-actions'
import { LightCourseCurriculumInterface } from '../light-course-interface'

export interface LectureActionsProps {
  moduleId: ModuleId
  lecture: LightCourseCurriculumInterface['modules'][number]['lectures'][number]
  mutate: KeyedMutator<LightCourseCurriculumInterface>
  onEdit: () => void
}

export const LightCourseLectureActions = memo(function LightCourseLectureActions({
  lecture,
  moduleId,
  mutate,
  onEdit,
}: LectureActionsProps) {
  const { t } = useLocoTranslation()
  const { deleteLightCourseLecture, activateLightCourseLecture } = useLightCourseActions()
  const { revalidateModules } = useRevalidateModules()
  const [isActivateOpen, setIsActivateOpen] = useState(false)
  const [isRemoveOpen, setIsRemoveOpen] = useState(false)
  const caption = t('dashboard.course.lecture_title').toLowerCase()
  const lectureId = lecture.id

  const handleActivateLecture = async () => {
    const update = lecture.active ? 'deactivate' : 'activate'
    await activateLightCourseLecture(update, lecture.id)
    await mutate(data => {
      if (data) {
        return {
          ...data,
          modules: data.modules.map(module => {
            if (module.id === moduleId) {
              return {
                ...module,
                lectures: module.lectures.map(lecture => {
                  if (lecture.id === lectureId) {
                    return { ...lecture, active: !lecture.active }
                  }
                  return lecture
                }),
              }
            }
            return module
          }),
        }
      }
    }, false)
  }

  const handleRemoveLecture = async () => {
    await deleteLightCourseLecture(lectureId)
    await mutate(data => {
      if (data) {
        return {
          ...data,
          modules: data.modules.map(module => {
            if (module.id === moduleId) {
              return {
                ...module,
                lectures: module.lectures.filter(lecture => lecture.id !== lectureId),
              }
            }
            return module
          }),
        }
      }
    }, false)
    await revalidateModules([RequestModuleEnum.MembershipCourseLecture])
  }

  return (
    <div className="flex items-center justify-end">
      <div {...nonDraggableProps}>
        <ActionMenu
          dataTestAttributes={{ 'data-test': 'module-lecture-actions' }}
          menuItems={[
            <ActionMenuItem
              key={'action-activate'}
              label={
                lecture.active
                  ? t('dashboard.actions.title.deactivate')
                  : t('dashboard.actions.title.activate')
              }
              onClick={() => {
                setIsActivateOpen(true)
              }}
              dataTestAttributes={{ 'data-test': 'module-lecture-action-status' }}
            />,

            <ActionMenuItem
              key={'action-edit'}
              label={t('dashboard.actions.title.settings')}
              onClick={() => onEdit()}
              dataTestAttributes={{ 'data-test': 'module-lecture-action-edit' }}
            />,
            <ActionMenuItem
              key={'action-remove'}
              label={t('dashboard.actions.title.remove')}
              onClick={() => {
                setIsRemoveOpen(true)
              }}
              dataTestAttributes={{ 'data-test': 'module-lecture-action-remove' }}
            />,
          ]}
        />
        <Confirmation
          opened={isRemoveOpen}
          onClose={() => setIsRemoveOpen(false)}
          onConfirm={handleRemoveLecture}
          confirmationText={t('dashboard.actions.remove_confirmation', {
            module: caption?.toLowerCase(),
          })}
          toastCaption={t('dashboard.actions.removed', { module: caption })}
        />
        <Confirmation
          opened={isActivateOpen}
          onClose={() => setIsActivateOpen(false)}
          onConfirm={handleActivateLecture}
          confirmationText={t('dashboard.actions.activate_confirmation', {
            activate: lecture.active
              ? t('global.deactivate').toLowerCase()
              : t('global.activate').toLowerCase(),
            module: caption?.toLowerCase(),
          })}
          toastCaption={t('dashboard.actions.activated', {
            module: caption,
            activated: lecture.active
              ? t('dashboard.actions.deactivated_caption').toLowerCase()
              : t('dashboard.actions.activated_caption').toLowerCase(),
          })}
        />
      </div>
    </div>
  )
})
